/**
 * Translation Center — real data. Lists the company's articles with the
 * languages their site publishes, shows which translations exist
 * (article_translations) and lets the editor request missing ones through
 * the translate-article edge function (credits: translate model, 2 to 5).
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Languages, RefreshCw, Sparkles, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const ALL_LANGS = ["fr", "en", "es", "ar", "de", "it", "pt", "nl", "tr"];

type Row = {
  id: string; title: string; language: string; status: string; website_id: string | null; created_at: string;
  translations: { language: string; translator: string | null; updated_at: string }[];
};

export default function TranslationCenterPage() {
  const { t } = useTranslation();
  const { currentCompanyId } = useCompany();
  const qc = useQueryClient();
  const [siteFilter, setSiteFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [extraLang, setExtraLang] = useState<string>("");

  const sitesQ = useQuery({
    queryKey: ["tc-sites", currentCompanyId],
    enabled: !!currentCompanyId,
    queryFn: async () => {
      const { data, error } = await supabase.from("websites").select("id, name, primary_language, languages, theme_config").eq("company_id", currentCompanyId!).order("name");
      if (error) throw error;
      return (data || []).map((s: any) => ({
        id: s.id, name: s.name, primary: s.primary_language || "fr",
        langs: ((s.theme_config?.languages as string[] | undefined) || s.languages || []) as string[],
      }));
    },
  });

  const articlesQ = useQuery({
    queryKey: ["tc-articles", currentCompanyId, siteFilter],
    enabled: !!currentCompanyId,
    queryFn: async () => {
      let q = supabase.from("articles").select("id, title, language, status, website_id, created_at, article_translations(language, translator, updated_at)")
        .eq("company_id", currentCompanyId!).in("status", ["approved", "scheduled", "published", "review"]).order("created_at", { ascending: false }).limit(200);
      if (siteFilter !== "all") q = q.eq("website_id", siteFilter);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map((a: any) => ({ ...a, translations: a.article_translations || [] })) as Row[];
    },
  });

  const siteLangs = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const s of sitesQ.data || []) m.set(s.id, s.langs.length ? s.langs : [s.primary]);
    return m;
  }, [sitesQ.data]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (articlesQ.data || []).filter((r) => !needle || r.title.toLowerCase().includes(needle)).map((r) => {
      const targets = new Set<string>((r.website_id && siteLangs.get(r.website_id)) || []);
      if (extraLang) targets.add(extraLang);
      targets.delete(r.language);
      const have = new Set(r.translations.map((x) => x.language));
      return { ...r, targets: Array.from(targets), missing: Array.from(targets).filter((l) => !have.has(l)) };
    });
  }, [articlesQ.data, siteLangs, search, extraLang]);

  const translate = useMutation({
    mutationFn: async ({ id, lang }: { id: string; lang: string }) => {
      const { data, error } = await supabase.functions.invoke("translate-article", { body: { article_id: id, target_language: lang } });
      if (error) throw new Error((data as any)?.error || error.message);
      if ((data as any)?.error) throw new Error((data as any).error?.message || (data as any).error);
      return data;
    },
    onSuccess: (_d, v) => { toast.success(t("translation.done", { lang: v.lang.toUpperCase() })); qc.invalidateQueries({ queryKey: ["tc-articles"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  const translateAllMissing = async (r: typeof rows[number]) => {
    for (const lang of r.missing) await translate.mutateAsync({ id: r.id, lang });
  };

  const stats = useMemo(() => {
    const total = rows.reduce((n, r) => n + r.targets.length, 0);
    const missing = rows.reduce((n, r) => n + r.missing.length, 0);
    return { total, done: total - missing, missing };
  }, [rows]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight"><Languages className="h-5 w-5" /> {t("translation.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("translation.subtitle")}</p>
        </div>
        <div className="flex gap-2 text-sm">
          <Badge variant="secondary">{t("translation.stats.done", { n: stats.done })}</Badge>
          <Badge variant={stats.missing ? "default" : "secondary"}>{t("translation.stats.missing", { n: stats.missing })}</Badge>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("translation.filters.title")}</CardTitle>
          <CardDescription>{t("translation.filters.description")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("translation.filters.allSites")}</SelectItem>
              {(sitesQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {(s.langs.length ? s.langs : [s.primary]).join(", ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="w-64" placeholder={t("translation.filters.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={extraLang || "none"} onValueChange={(v) => setExtraLang(v === "none" ? "" : v)}>
            <SelectTrigger className="w-56"><SelectValue placeholder={t("translation.filters.extraLang")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("translation.filters.extraLangNone")}</SelectItem>
              {ALL_LANGS.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => articlesQ.refetch()}><RefreshCw className="h-4 w-4" /></Button>
        </CardContent>
      </Card>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">{t("translation.table.article")}</th>
              <th className="px-3 py-2 text-left">{t("translation.table.source")}</th>
              <th className="px-3 py-2 text-left">{t("translation.table.editions")}</th>
              <th className="px-3 py-2 text-right">{t("translation.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="max-w-md truncate px-3 py-2">
                  <a className="hover:underline" href={`/articles/${r.id}`}>{r.title}</a>
                  <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()} · {r.status}</div>
                </td>
                <td className="px-3 py-2"><Badge variant="outline">{r.language.toUpperCase()}</Badge></td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {r.targets.length === 0 && <span className="text-xs text-muted-foreground">{t("translation.table.monolingual")}</span>}
                    {r.targets.map((l) => {
                      const has = r.translations.find((x) => x.language === l);
                      return (
                        <Badge key={l} variant={has ? "default" : "secondary"} className="gap-1">
                          {has ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{l.toUpperCase()}
                        </Badge>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.missing.length > 0 && (
                    <Button size="sm" variant="outline" disabled={translate.isPending} onClick={() => translateAllMissing(r)}>
                      <Sparkles className="mr-1 h-3.5 w-3.5" />{t("translation.table.translateMissing", { n: r.missing.length })}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !articlesQ.isLoading && (
              <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-muted-foreground">{t("translation.table.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{t("translation.footnote")}</p>
    </div>
  );
}
